import { useEffect } from 'react';
import Header from '../../../components/base/Header';
import Footer from '../../../components/base/Footer';
import { Link } from 'react-router-dom';

export default function WelcomeBlog() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-orange-50">
      <Header />

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-r from-pink-500 to-rose-500">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <i className="ri-heart-fill text-5xl text-white"></i>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              【初投稿】ようこそ、モモちゃん畑へ。
            </h1>
            <p className="text-xl text-white/90 mb-6">
              ──花と心がやさしくつながる場所──
            </p>
            <div className="flex items-center justify-center gap-4 text-white/90">
              <span className="flex items-center">
                <i className="ri-calendar-line mr-2"></i>
                2025年11月1日
              </span>
              <span className="flex items-center">
                <i className="ri-user-line mr-2"></i>
                ももちゃん
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/10c749ea00a4d71cc899a8ae5bfe0400.jpeg"
                alt="モモちゃん畑へようこそ"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                こんにちは。モモちゃん畑の管理人、ももちゃんです。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                今日からこのブログをスタートします。<br />
                はじめての投稿では、<br />
                「どうしてモモちゃん畑を作ったのか？」<br />
                そのコンセプトをお話ししたいと思います。
              </p>
            </div>

            {/* Section 1 */}
            <div className="mb-12 bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-rose-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌸</span>
                花と生きることで、心が救われた瞬間があったから
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  私はこれまで、息子と一緒にたくさんの植物を育ててきました。
                </p>
                <p>
                  シクラメン、エアープランツ、胡蝶蘭……<br />
                  息子が好きな花たちは、どれも個性的で、<br />
                  それなのにふわっと心を癒してくれる存在ばかり。
                </p>
                <p>
                  忙しい日、うまくいかない日、<br />
                  ちょっと自信をなくしそうな日。
                </p>
                <p>
                  そんなときでも、<br />
                  ベランダにあるたった一輪の花が、<br />
                  そっと背中を押してくれることがありました。
                </p>
                <div className="bg-white rounded-2xl p-6 my-6 border-l-4 border-rose-400">
                  <p className="text-rose-700 font-medium">
                    「あ、今日もちゃんと咲いている。」<br />
                    「この子も頑張ってる。」
                  </p>
                </div>
                <p>
                  そう思うだけで、心が少しやわらかくなる。
                </p>
                <p className="font-medium text-rose-800">
                  私にとって植物は、<br />
                  "言葉を持たない小さな先生" のような存在です。
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-green-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌱</span>
                キャラクターたちは、花から生まれた「心の物語」
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  モモちゃん畑には、植物をモチーフにした仲間たちがいます。
                </p>
                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-pink-600 mb-2">優美で清純な 蘭ちゃん</h3>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-teal-600 mb-2">自由気ままな エアーくん</h3>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-red-600 mb-2">情熱的な ローズくん</h3>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-rose-600 mb-2">やさしさを運ぶシクラメンの シッくんと胡蝶蘭の蘭々</h3>
                  </div>
                </div>
                <p>
                  彼らはただのキャラではなく、<br />
                  花が持つ心のメッセージを小さな物語として届けてくれる存在です。
                </p>
                <p>
                  花言葉は、<br />
                  「頑張れ」といわなくても伝わる、<br />
                  静かなエールのようなもの。
                </p>
                <p className="font-medium text-green-800">
                  キャラたちのストーリーを通して、<br />
                  読む人の心がふっと軽くなるような、<br />
                  そんな場所にしたいと思っています。
                </p>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-12 bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-orange-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌷</span>
                モモちゃん畑でお届けするもの
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="mb-6">
                  このブログでは、次のような内容を発信していきます。
                </p>
                
                <div className="space-y-6">
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-orange-700 mb-3 flex items-center">
                      <span className="mr-2">🔸</span>
                      花言葉 × 物語（オリジナルストーリー）
                    </h3>
                    <p>
                      花言葉がもっと心に届くように、<br />
                      キャラたちがやさしく案内する短いストーリー。
                    </p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-orange-700 mb-3 flex items-center">
                      <span className="mr-2">🔸</span>
                      47都道府県の花文化
                    </h3>
                    <p>
                      それぞれの土地の花言葉や由来を、<br />
                      温度のある言葉で紹介します。
                    </p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-orange-700 mb-3 flex items-center">
                      <span className="mr-2">🔸</span>
                      季節の花の楽しみ方
                    </h3>
                    <p>
                      春夏秋冬、日々の暮らしに寄り添う花の豆知識。
                    </p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-orange-700 mb-3 flex items-center">
                      <span className="mr-2">🔸</span>
                      モモちゃんの花育日記
                    </h3>
                    <p>
                      息子と育ててきた経験から、<br />
                      "花と暮らす楽しさ"をリアルにお伝えします。
                    </p>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <h3 className="text-xl font-bold text-orange-700 mb-3 flex items-center">
                      <span className="mr-2">🔸</span>
                      毎日のおみくじ（花言葉メッセージ）
                    </h3>
                    <p>
                      1日1つ、小さな花ことばが心に届く習慣。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="mb-12 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-teal-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌿</span>
                忙しい人のための「心の休憩場所」に
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  私たちは毎日、<br />
                  仕事、家事、子育て、人間関係…<br />
                  たくさんのことに向き合っています。
                </p>
                <p>
                  ときには疲れてしまう日もある。
                </p>
                <p>
                  そんなとき、<br />
                  ここに来て花たちとキャラたちに触れていただけたら、<br />
                  ほんの少しでも心が軽くなるような、<br />
                  "やさしい休憩所"にしたい。
                </p>
                <div className="bg-white rounded-2xl p-8 my-6 text-center border-2 border-teal-200">
                  <p className="text-2xl font-bold text-teal-800">
                    それが、モモちゃん畑の願いです。
                  </p>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-12">
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #初投稿
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #コンセプト
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #モモちゃん畑
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #花言葉
              </span>
              <span className="px-4 py-2 bg-rose-100 text-rose-700 text-sm rounded-full">
                #キャラクター
              </span>
            </div>

            {/* Back Button */}
            <div className="text-center">
              <Link
                to="/essay"
                className="inline-flex items-center bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                エッセイ一覧に戻る
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      <section className="py-16 bg-gradient-to-br from-pink-50 to-rose-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-rose-800 mb-8 text-center">
              他のブログ記事も読む
            </h2>
            <div className="text-center">
              <Link
                to="/essay"
                className="inline-flex items-center text-rose-600 hover:text-rose-700 font-medium transition-colors"
              >
                すべての記事を見る
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
